import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  PREFILL_SEARCH_DEBOUNCE_MS,
  PREFILL_STATUS_OPTIONS,
  curatorErrorCopy,
  downloadCuratorExport,
  parsePrefillFilters,
  rowStatusView,
  useCuratorPrefill,
  type CuratorPrefillRow,
} from '../../api/curator';
import { useCuratorGroups, useCuratorGroupsScope } from '../../api/curator-groups';
import { isPrefillWorkingRow, type CuratorChild, type CuratorPageMeta } from '../../api/curator-model';
import { ApiError } from '../../api/client';
import { EmptyState, ErrorState, Input, Select, Spinner, Badge, Button, Pagination } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { formatDateTimeMsk } from '../dashboard/format';
import { importNoticeLine } from '../../api/curator-model';
import {
  CABINET_PAGE_SIZE_OPTIONS,
  PREFILL_BUCKET_PARAM,
  PREFILL_DICTIONARY_ERROR,
  PREFILL_SORT_COLUMNS,
  cabinetRangeCaption,
  groupFilterOptions,
  nextSortDirection,
  isOfflineFormatRow,
  normalizeCabinetPageSize,
  OFFLINE_ROW_HINT,
  offlineFormatNotice,
  parsePrefillBuckets,
  prefillCourseOptions,
  prefillDictionaryState,
  prefillRowMatchesBuckets,
  rowGroupLabel,
  sortPrefillRows,
  type PrefillBucket,
  type PrefillSortKey,
  type SortDirection,
} from './cabinet-model';
// ADDITIVE (v1.9.64, T.Z195) — the chase cells, ONE component shared with the offline
// roster so the two children tables cannot drift.
import { ChaseCells, ChaseColumnHeaders } from './ChaseCells';
import { useCabinetPageSize } from './useCabinetPageSize';
import { usePrograms } from '../quotas/dictionary';
import { AUDIENCE_FILTER_OPTIONS, AUDIENCE_LABEL } from '../applications/applications-model';
import { OfflineChildDrawer } from './OfflineChildDrawer';
import { PrefillDeleteDrawer, PrefillFormDrawer } from './PrefillFormDrawer';
import { PrefillImportModal } from './PrefillImportModal';
import './cabinet.css';

/**
 * The cabinet's online half (#26.2–#26.6) as a STANDALONE block — the code that
 * used to live inside `CabinetPage` (T.Y18/T.Z14), moved out in T.Z27 so a second
 * surface can render the SAME table, filters, drawers and export without
 * duplicating any of it.
 *
 * The second surface is the venue card's «Онлайн-набор» (`/venues/:venueId/prefill`):
 * a МЕНЕДЖЕР linked to a flagged ONLINE venue manages that venue's set exactly like a
 * curator does (owner model, 2026-09-13 — «Куратор площадки» stays external-only, the
 * МЕНЕДЖЕР never sees the curator cabinet, but the flagged venue's set is theirs to
 * keep). Every read and write below goes to `#26.2–#26.6`, which the api authorises
 * for a manager with flagged links and an ADMIN — nothing here calls `#26` or
 * `#26.1`, the two routes the offline roster keeps to the КУРАТОР + АДМИН pair.
 *
 * The scope arrives through PROPS on purpose: the curator cabinet scopes itself by
 * its own picker + `venueId: null` aggregate, the venue page pins one venue — neither
 * reads a session-level cabinet context, so the block cannot inherit the wrong set.
 *
 * ADDITIVE (v1.9.26, T.Z125) — the read is a UNION now: the venue's working rows
 * PLUS the venue's ONLINE-set children (`population: 'ONLINE_ROSTER'`, read-only — the
 * system's own applications, the rows a venue-report import or the matcher produce).
 * A working row opens the edit drawer as always; an ONLINE-set row opens the SAME
 * child drawer the offline roster uses (its one editable fact is the учебная группа,
 * #26.12/#26.13 by the row's `applicationId`) — but only on a venue view, where the
 * group list can be scoped; on «Все места» the row renders read-only and inert.
 */
export function PrefillOnlineBlock({
  venueId,
  venueName,
  canAdd,
  heading = 'Онлайн (мой набор)',
  sub,
}: {
  /** The venue the table is scoped to, or `null` for the cabinet's whole scope. */
  venueId: number | null;
  /** The display name of the scope — one venue, or «Все ваши площадки». */
  venueName: string;
  /** The create affordance (`allowCuratorChildren` of the venue — Р5 needs ONE venue). */
  canAdd: boolean;
  /** The block's title. The cabinet keeps its own; the venue page names the set. */
  heading?: string;
  /** The line under the title; the cabinet's default describes its own editing rule. */
  sub?: string;
}) {
  const [params, setParams] = useSearchParams();
  // T.Z119 — the page size when the URL is silent is the user's stored choice, 20 when
  // there is none. The hook is the SAME store the offline block reads, so a choice
  // made in either selector is live in both; the URL still wins while it speaks.
  const [storedPageSize, chooseStoredPageSize] = useCabinetPageSize();
  const filters = useMemo(
    () => parsePrefillFilters(Object.fromEntries(params.entries()), { defaultPageSize: storedPageSize }),
    [params, storedPageSize],
  );
  const buckets = useMemo(() => parsePrefillBuckets(params.get(PREFILL_BUCKET_PARAM)), [params]);
  const query = useCuratorPrefill({ ...filters, venueId: venueId ?? undefined });
  // ADDITIVE (v1.8.8, T.Z43) — the course dictionary (#21) behind «Программа»: one
  // option per course, captioned by the owner's short name.
  const programs = usePrograms();
  // T.Z64 — a FAILED read is spoken, not folded into an empty list: the filter is
  // disabled with the error line (and a retry) rather than reading as «no programmes».
  const dictionaryState = prefillDictionaryState(programs);
  const courseOptions = prefillCourseOptions(programs.data?.data ?? []);
  // ADDITIVE (v1.9.6, T.Z62) — the «Группа» filter (#26.8): the venue's groups on a
  // venue view, the scope's on «Все места» (captioned with the venue there, since two
  // filials may name a group alike). The filter is SERVER-side (`?groupId=`), so the
  // list only captions the choices — the api does the narrowing. Two hooks, one idle:
  // the venue-scoped read stays `enabled: venueId !== null` (the group section's own
  // rule), the scope read only wakes up on «Все места».
  const venueGroups = useCuratorGroups(venueId);
  const scopeGroups = useCuratorGroupsScope();
  const groupList = venueId !== null ? venueGroups.data ?? [] : scopeGroups.data ?? [];
  const groupOptions = groupFilterOptions(
    groupList.map((group) => ({ id: group.id, name: group.name, venueName: group.venueName })),
    { wholeScope: venueId === null },
  );

  // Client-side sort of the LOADED page (T.Y18): the columns are sortable, the api
  // has no `sort` parameter, and a page change refetches and re-sorts what arrived.
  const [sort, setSort] = useState<{ key: PrefillSortKey; direction: SortDirection } | null>(null);
  const [formRow, setFormRow] = useState<'new' | CuratorPrefillRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<CuratorPrefillRow | null>(null);
  // ADDITIVE (v1.9.26, T.Z125) — the ONLINE-set row a click opened as a child drawer
  // (the offline roster's own drawer: the group is the one editable fact).
  const [rosterChild, setRosterChild] = useState<CuratorChild | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // ADDITIVE (v1.8.8, T.Z43) — `courseId`/`audience` join the keys the row owns.
  // ADDITIVE (v1.9.6, T.Z62) — `groupId` joins them: the «Группа» filter, server-side.
  const setFilter = (key: 'status' | 'q' | 'pageSize' | 'courseId' | 'audience' | 'groupId') => (value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    // A filter change restarts the list: page 3 of a narrower result is an empty
    // screen nobody asked for.
    next.delete('page');
    setParams(next, { replace: true });
  };

  // C5 — the search box. The draft belongs to the keystroke, the URL to the pause:
  // 300 ms after the last character the trimmed value lands in `?q=` and the read
  // refires server-side (contract v1.8.2). An external change to `?q=` (a shared
  // link, «Сбросить фильтры») always wins over the draft.
  const searchValue = filters.q ?? '';
  const [searchDraft, setSearchDraft] = useState(searchValue);
  const setSearch = useCallback((value: string) => setFilter('q')(value), [params, setParams]);
  useEffect(() => setSearchDraft(searchValue), [searchValue]);
  useEffect(() => {
    if (searchDraft.trim() === searchValue) return;
    const timer = window.setTimeout(() => setSearch(searchDraft.trim()), PREFILL_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchDraft, searchValue, setSearch]);

  const sorted = useMemo(
    () => (sort === null ? query.data?.rows ?? [] : sortPrefillRows(query.data?.rows ?? [], sort.key, sort.direction)),
    [query.data, sort],
  );
  // The chips slice the LOADED page (C2): neither `MATCHED` nor the merged
  // «Не найдено» is a single `?status=` value the api could take, so the two
  // buckets are cut client-side, on the page the server already answered.
  const rows = useMemo(() => sorted.filter((row) => prefillRowMatchesBuckets(row, buckets)), [sorted, buckets]);
  const meta: CuratorPageMeta | null = query.data?.meta ?? null;
  // ADDITIVE (v1.9.46, T.Z166) — the офлайн notice's sentence, `null` when the count
  // is 0 or the api predates the key (the notice then stays off entirely).
  const offlineNotice = useMemo(() => offlineFormatNotice(meta?.offlineFormatMatched), [meta]);
  const pageMeta =
    meta === null
      ? null
      : {
          page: meta.page,
          total: meta.total,
          limit: meta.pageSize,
          totalPages: Math.max(1, Math.ceil(meta.total / Math.max(1, meta.pageSize))),
        };

  const openRow = (row: CuratorPrefillRow) => {
    setNotice(null);
    setFormRow(row);
  };

  /** The ONLINE-set row a click opens as a child (T.Z125) — only where it may speak. */
  const openRosterRow = (row: CuratorPrefillRow) => {
    setNotice(null);
    setRosterChild(rosterChildOf(row));
  };

  const askDelete = (row: CuratorPrefillRow) => {
    setNotice(null);
    setDeleteRow(row);
  };

  return (
    <section className="card card--flush cabinet-block" data-testid="cabinet-online" aria-label="Онлайн-набор">
      <header className="cabinet-block__head cabinet-block__head--row">
        <div>
          <h2>{heading}</h2>
          <p className="cabinet-block__sub">
            {sub ??
              // v1.9.26 (T.Z125) — the table carries TWO populations now: the curator's
              // own working rows (editable until the match) and the venue's ONLINE-set
              // children (the system's own rows — read-only, the group is the one fact
              // the drawer takes). The line names both, so «править» never promises
              // what a row cannot do.
              `${venueName} · свою строку можно открыть кликом и править, пока ребёнок не сопоставлен; строки платформы — только чтение (группу назначить можно).`}
          </p>
        </div>
        <div className="cabinet-block__actions">
          {canAdd ? (
            <>
              <Button
                variant="primary"
                size="sm"
                data-testid="prefill-add"
                onClick={() => {
                  setNotice(null);
                  setFormRow('new');
                }}
              >
                Добавить ребёнка
              </Button>
              {/* ADDITIVE (v1.8.9, T.Z46) — the file door sits with the add button it
                  accelerates: ONE venue per file (the api's #26.7 rule), so the button
                  exists only where that venue is pinned — never on «Все места».
                  T.Z63 renamed it (the owner's wording, typo and all). */}
              {venueId !== null ? (
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="prefill-import"
                  onClick={() => {
                    setNotice(null);
                    setImportOpen(true);
                  }}
                >
                  Загрузить из файла
                </Button>
              ) : null}
            </>
          ) : null}
          {/* Z5 — the export lives HERE, under the venue it exports. */}
          <ExportButton venueId={venueId ?? undefined} />
        </div>
      </header>

      <div className="filter-bar" role="search" aria-label="Фильтры строк">
        <div className="filter-bar__row">
          <div className="filter-bar__field">
            <label className="filter-bar__label" htmlFor="prefill-status">
              Статус
            </label>
            {/* The funnel only (C6): the two match states left the UI — the merged
                «Не найдено» bucket is the summary chips' job, and one `?status=`
                value cannot say «NOT_FOUND or AMBIGUOUS» anyway. A matched row still
                filters by the very status the table renders. */}
            <Select
              id="prefill-status"
              data-testid="prefill-status"
              value={filters.status ?? ''}
              onChange={(event) => setFilter('status')(event.target.value)}
            >
              <option value="">Все статусы</option>
              {PREFILL_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          {/* ADDITIVE (v1.8.8, T.Z43) — «Программа»: the row's RESOLVED course, the
              value the api stores (`course_id`), captioned by the short name.
              T.Z64 — with a FAILED dictionary read the select is disabled and the
              failure is said out loud with a retry, never disguised as an empty
              dictionary. */}
          <div className="filter-bar__field">
            <label className="filter-bar__label" htmlFor="prefill-course">
              Программа
            </label>
            <Select
              id="prefill-course"
              data-testid="prefill-course"
              value={filters.courseId === undefined ? '' : String(filters.courseId)}
              disabled={dictionaryState.kind === 'error'}
              onChange={(event) => setFilter('courseId')(event.target.value)}
            >
              <option value="">Все программы</option>
              {courseOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                  {option.hint === undefined ? '' : ` (${option.hint})`}
                </option>
              ))}
            </Select>
            {dictionaryState.kind === 'error' ? (
              <p className="cabinet-form__hint" role="alert" data-testid="prefill-course-error">
                {PREFILL_DICTIONARY_ERROR}{' '}
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="prefill-course-retry"
                  loading={programs.isFetching}
                  onClick={() => void programs.refetch()}
                >
                  Повторить
                </Button>
              </p>
            ) : null}
          </div>
          {/* ADDITIVE (v1.8.8, T.Z43) — «Аудитория»: the shared three buckets, resolved
              through the row's course server-side; course-less rows match neither. */}
          <div className="filter-bar__field filter-bar__field--narrow">
            <label className="filter-bar__label" htmlFor="prefill-audience">
              Аудитория
            </label>
            <Select
              id="prefill-audience"
              data-testid="prefill-audience"
              value={filters.audience ?? ''}
              onChange={(event) => setFilter('audience')(event.target.value)}
            >
              <option value="">Любая</option>
              {AUDIENCE_FILTER_OPTIONS.map((audience) => (
                <option key={audience} value={audience}>
                  {AUDIENCE_LABEL[audience] ?? audience}
                </option>
              ))}
            </Select>
          </div>
          {/* ADDITIVE (v1.9.6, T.Z62) — «Группа»: the venue's учебные группы, resolved
              SERVER-side (`?groupId=`, the `none` bucket = «без группы», every
              unmatched row included). AND-composed with the other filters by the api. */}
          <div className="filter-bar__field">
            <label className="filter-bar__label" htmlFor="prefill-group">
              Группа
            </label>
            <Select
              id="prefill-group"
              data-testid="prefill-group"
              value={filters.groupId === undefined ? '' : String(filters.groupId)}
              onChange={(event) => setFilter('groupId')(event.target.value)}
            >
              {groupOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="filter-bar__field filter-bar__field--search">
            <label className="filter-bar__label" htmlFor="prefill-search">
              Поиск
            </label>
            {/* C5 — server-side (contract v1.8.2): one OR over the ФИО, the номер
                заявления and the УНТИ id. No contacts: the table has none to give. */}
            <Input
              id="prefill-search"
              data-testid="prefill-search"
              type="search"
              value={searchDraft}
              placeholder="Поиск: ФИО, ЕПГУ id, УНТИ id"
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </div>
          <div className="filter-bar__field">
            <label className="filter-bar__label" htmlFor="prefill-pagesize">
              Строк на странице
            </label>
            {/* T.Z119 — the choice persists per browser and is shared with the offline
                table's selector (the same store); the URL keeps the view's own value
                for shared links. Picking a size restarts the list, like any filter. */}
            <Select
              id="prefill-pagesize"
              data-testid="prefill-pagesize"
              value={String(filters.pageSize)}
              onChange={(event) => {
                const size = normalizeCabinetPageSize(event.target.value);
                chooseStoredPageSize(size);
                setFilter('pageSize')(String(size));
              }}
            >
              {CABINET_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </div>
          {hasActivePrefillFilters(filters, buckets) ? (
            <Button size="sm" variant="ghost" data-testid="prefill-reset" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Сбросить фильтры
            </Button>
          ) : null}
        </div>
      </div>

      {notice !== null ? (
        <p className="cabinet-notice" role="status" data-testid="prefill-notice">
          {notice}
        </p>
      ) : null}

      {query.isPending ? (
        <Spinner label="Загружаем строки" />
      ) : query.isError ? (
        <ErrorState message={copyOf(query.error)} onRetry={() => void query.refetch()} />
      ) : (
        <>
          {/* ADDITIVE (v1.9.46, T.Z166) — the офлайн notice, ABOVE the table: N of the
              scope's children already stand in an офлайн-таблице (the api's
              `meta.offlineFormatMatched`, filter- and page-independent). It speaks the
              standing rule, the red rows below point at the children; the REMOVE stays
              the curator's own action (#26.4). An api that predates the key sends no
              count and nothing renders — never a «0 детей». */}
          {offlineNotice !== null ? (
            <p className="cabinet-notice cabinet-notice--offline" role="status" data-testid="prefill-offline-notice">
              {offlineNotice}
            </p>
          ) : null}
          {buckets.length > 0 && sorted.length > rows.length ? (
            <p className="cabinet-notice" data-testid="prefill-bucket-note">
              Показаны строки выбранного фильтра на этой странице: {rows.length} из {sorted.length}.
            </p>
          ) : null}
          <PrefillTable
            rows={rows}
            filtered={buckets.length > 0}
            sort={sort}
            onSort={(key) => setSort((current) => ({ key, direction: nextSortDirection(current, key) }))}
            onOpen={openRow}
            onOpenRoster={venueId !== null ? openRosterRow : undefined}
          />
        </>
      )}

      {/* T.Z119 — the pager speaks whenever the read has rows, one page included
          (the caption IS the count); an empty table keeps its EmptyState alone. */}
      {pageMeta !== null && pageMeta.total > 0 ? (
        <Pagination
          meta={pageMeta}
          // T.Z119 — the caption speaks the table's own words («строки», the bucket
          // note's noun) with the server's own numbers: the page the api answered, not
          // the slice the chips cut afterwards.
          rangeCaption={cabinetRangeCaption('строки', pageMeta.page, pageMeta.total, pageMeta.limit)}
          onPage={(page) => {
            const next = new URLSearchParams(params);
            if (page > 1) next.set('page', String(page));
            else next.delete('page');
            setParams(next, { replace: true });
          }}
        />
      ) : null}

      {formRow !== null && deleteRow === null ? (
        <PrefillFormDrawer
          venueName={venueName}
          venueId={venueId ?? undefined}
          row={formRow === 'new' ? undefined : formRow}
          // T.Z58 — the table's active programme/audience filters seed a NEW-child form:
          // a row added while the table is narrowed to «ИИ СПО · офл» belongs to that
          // same slice. An edit always shows the row's own stored fields instead.
          filters={formRow === 'new' ? { courseId: filters.courseId, audience: filters.audience } : undefined}
          onClose={() => setFormRow(null)}
          onSaved={(message) => {
            setFormRow(null);
            setNotice(message);
          }}
          onDelete={askDelete}
        />
      ) : null}

      {/* ADDITIVE (v1.9.26, T.Z125) — the ONLINE-set row's own drawer: the SAME child
          drawer the offline roster opens (its one editable fact is the учебная группа,
          #26.12/#26.13 by `applicationId`), so a system row of this table has a real
          action instead of a dead click. The invalidate in the group hooks refetches
          the block, so the table's «Группа» cell follows the write. */}
      {rosterChild !== null && venueId !== null ? (
        <OfflineChildDrawer
          venueId={venueId}
          venueName={venueName}
          child={rosterChild}
          onClose={() => setRosterChild(null)}
          onSaved={(message) => {
            setRosterChild(null);
            setNotice(message);
          }}
        />
      ) : null}

      {deleteRow !== null ? (
        <PrefillDeleteDrawer
          venueName={venueName}
          row={deleteRow}
          onClose={() => setDeleteRow(null)}
          onDeleted={(message) => {
            // T.Z24 (owner, live testing): a delete that worked closes BOTH layers —
            // the confirmation AND the row drawer that asked for it. Only clearing
            // `deleteRow` left `formRow` standing, so the form reappeared over the
            // row that no longer exists. The invalidate in the hook refetches the
            // list, and the notice is what tells the curator it worked.
            setFormRow(null);
            setDeleteRow(null);
            setNotice(message);
          }}
        />
      ) : null}

      {/* ADDITIVE (v1.8.9, T.Z46) — the file door (a wide modal since T.Z63). It keeps
          itself open over its report (the answer is the feedback); the block refetches
          the moment the file lands, so the table below is already the new truth. */}
      {importOpen ? (
        <PrefillImportModal
          venueId={venueId as number}
          venueName={venueName}
          onClose={() => setImportOpen(false)}
          onImported={(report) => setNotice(importNoticeLine(report))}
        />
      ) : null}
    </section>
  );
}

/** A filter counts as active when the URL carries it — the chips included (C2). */
function hasActivePrefillFilters(
  filters: { status?: string; q?: string; groupId?: string | number },
  buckets: readonly PrefillBucket[],
): boolean {
  return filters.status !== undefined || filters.q !== undefined || filters.groupId !== undefined || buckets.length > 0;
}

/**
 * The rows of «мой набор». T.Y18: the row itself is the button (click → the edit
 * drawer), the inline «Править»/«Удалить» are gone, and ФИО / Номер заявления /
 * УНТИ ID sort — `sort` drives the arrow, `onSort` flips it. T.Z14/C3: no ФИО-format
 * badge — the «не в формате» fact belongs to the ADMIN registry (#27), not to a
 * table a curator reads by ФИО.
 *
 * ADDITIVE (v1.9.26, T.Z125) — the widened #26.2 rows carry a `source`: a `'PREFILL'`
 * row opens the edit drawer exactly as before; an `'ONLINE_ROSTER'` row is the
 * venue's own system child — read-only, so it opens the child drawer (the group
 * select) where the venue is pinned ({@link onOpenRoster}) and renders inert on the
 * aggregate view, where a group list has no venue to scope to.
 */
function PrefillTable({
  rows,
  filtered,
  sort,
  onSort,
  onOpen,
  onOpenRoster,
}: {
  rows: readonly CuratorPrefillRow[];
  filtered: boolean;
  sort: { key: PrefillSortKey; direction: SortDirection } | null;
  onSort: (key: PrefillSortKey) => void;
  onOpen: (row: CuratorPrefillRow) => void;
  /** Present on a venue view: an ONLINE-set row may open the child drawer there. */
  onOpenRoster?: (row: CuratorPrefillRow) => void;
}) {
  if (rows.length === 0) {
    return filtered ? (
      <EmptyState
        title="Под фильтр строк не подошло"
        hint="На этой странице таких строк нет. Снимите фильтр над таблицей или найдите ребёнка через поиск."
      />
    ) : (
      <EmptyState
        title="Строк пока нет"
        hint="Добавьте ребёнка вручную — сопоставление пройдёт при следующей проверке."
      />
    );
  }

  return (
    <TableWrap>
      <table className="data" data-testid="prefill-table">
        <thead>
          <tr>
            {PREFILL_SORT_COLUMNS.map((column, index) => (
              <th
                key={column.key}
                scope="col"
                className="cabinet-sort"
                data-testid={`prefill-sort-col-${column.key}`}
                aria-sort={ariaSortOf(sort, column.key)}
              >
                <button
                  type="button"
                  className="cabinet-sort__button"
                  data-testid={`prefill-sort-${column.key}`}
                  onClick={() => onSort(column.key)}
                >
                  {column.label}
                  <span className="cabinet-sort__arrow" aria-hidden="true">
                    {sort?.key === column.key ? (sort.direction === 'asc' ? '↑' : '↓') : index === 0 ? '↕' : ''}
                  </span>
                </button>
              </th>
            ))}
            <th scope="col">Регион</th>
            <th scope="col">Программа</th>
            {/* ADDITIVE (v1.9.6, T.Z62) — the «Группа» cell: the учебная группа the
                row's child sits in, «—» while it is in none (every unmatched row). */}
            <th scope="col">Группа</th>
            <th scope="col">Статус</th>
            {/* ADDITIVE (v1.9.64, T.Z195) — the chase columns after the status: a
                matched (or ONLINE-set) child's contract, deadline, enrollment and the
                «Провалил ЕВИ» fact — the register's cells, one shared component with
                the offline roster. An unmatched working row answers «—» honestly. */}
            <ChaseColumnHeaders />
            {/* ADDITIVE (v1.9.83, T.Z227) — the ONLINE-set child's contacts, the
                owner-ruled surface (owner 2026-10-05: «только в кабинеты кураторам
                ОНЛАЙН»). Captions EXACTLY the #26.6 file's headers, appended after the
                chase columns in the file's own order. An ONLINE-set row prints its
                own values («—» where the export held nothing); a working row carries
                no contact key at all and prints the dash — the offline roster's table
                gains NO column. */}
            <th scope="col">Телефон</th>
            <th scope="col">E-mail</th>
            <th scope="col">Проверено</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const status = rowStatusView(row);
            // ADDITIVE (v1.9.46, T.Z166) — the офлайн flag: a matched row whose child's
            // application is OFFLINE. The api says so (`matchedApplicationFormat`), the
            // row is tinted with the theme's danger token (dark-theme safe) and the
            // hint names the fix; the remove stays the curator's own act.
            const offline = isOfflineFormatRow(row);
            // ADDITIVE (v1.9.26, T.Z125) — the read-only population: the drawer a click
            // opens is the child drawer (venue view only), the edit drawer never.
            const roster = !isPrefillWorkingRow(row);
            const rosterOpenable = roster === true && onOpenRoster !== undefined && row.currentStatus !== undefined && row.applicationId !== null;
            const openable = roster ? rosterOpenable : true;
            return (
              <tr
                key={row.id}
                data-testid="prefill-row"
                data-row-id={row.id}
                data-population={row.population ?? 'PREFILL'}
                data-match={row.matchStatus ?? 'MATCHED'}
                data-offline-format={offline || undefined}
                className={[
                  openable ? 'cabinet-row--clickable' : null,
                  offline ? 'cabinet-row--offline' : null,
                ]
                  .filter((name): name is string => name !== null)
                  .join(' ') || undefined}
                tabIndex={openable ? 0 : undefined}
                aria-label={roster ? `Открыть ребёнка: ${row.fullName}` : `Открыть строку: ${row.fullName}`}
                onClick={openable ? () => (roster ? onOpenRoster?.(row) : onOpen(row)) : undefined}
                onKeyDown={
                  openable
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          if (roster) onOpenRoster?.(row);
                          else onOpen(row);
                        }
                      }
                    : undefined
                }
              >
                <td className="cell-primary">{row.fullName}</td>
                <td className={row.pguId === null ? 'cell-muted' : undefined}>{row.pguId ?? '—'}</td>
                <td className={row.untiId === null ? 'cell-muted' : undefined}>{row.untiId ?? '—'}</td>
                <td className={row.region === null ? 'cell-muted' : undefined}>{row.region ?? '—'}</td>
                <td className={row.programName === null ? 'cell-muted' : undefined}>{row.programName ?? '—'}</td>
                <td className={row.group === null ? 'cell-muted' : undefined} data-testid={`prefill-group-${row.id}`}>
                  {rowGroupLabel(row.group)}
                </td>
                <td data-testid={`prefill-status-${row.id}`}>
                  <Badge tone={status.tone} title={status.label}>
                    {status.label}
                  </Badge>
                  {status.missReasonLabel !== null ? (
                    <span className="cell-sub" data-testid={`prefill-miss-${row.id}`}>
                      {status.missReasonLabel}
                    </span>
                  ) : null}
                  {offline ? (
                    <span className="cell-sub cabinet-offline-hint" data-testid={`prefill-offline-hint-${row.id}`}>
                      {OFFLINE_ROW_HINT}
                    </span>
                  ) : null}
                </td>
                {/* ADDITIVE (v1.9.64, T.Z195) — the chase cells of the matched child. */}
                <ChaseCells row={row} id={row.id} />
                {/* ADDITIVE (v1.9.83, T.Z227) — the contacts of the ONLINE-set row
                    (the keys the api serves there and nowhere else): verbatim, «—» on
                    null/absent — the file's own cell semantics, so the two cannot
                    drift. */}
                <td className={row.phone ? undefined : 'cell-muted'} data-testid={`prefill-phone-${row.id}`}>
                  {row.phone ?? '—'}
                </td>
                <td className={row.email ? undefined : 'cell-muted'} data-testid={`prefill-email-${row.id}`}>
                  {row.email ?? '—'}
                </td>
                <td className="cell-compact">{formatDateTimeMsk(row.lastCheckedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableWrap>
  );
}

/**
 * The ONLINE-set row as the child drawer's `CuratorChild` (v1.9.26, T.Z125) — the same
 * shape #26.1's roster answers, folded from the widened #26.2 row. The drawer is only
 * offered where the row actually has the facts it needs (`currentStatus`,
 * `applicationId` — the caller's gate), so the fallbacks below are defensive shapes,
 * never a silent lie on the screen.
 */
function rosterChildOf(row: CuratorPrefillRow): CuratorChild {
  return {
    fullName: row.fullName,
    region: row.region,
    programName: row.programName,
    currentStatus: row.currentStatus ?? '',
    pguId: row.pguId ?? '',
    untiId: row.untiId,
    applicationId: row.applicationId,
    group: row.group,
    // ADDITIVE (v1.9.64, T.Z195) — the chase facts ride along: an ONLINE-set row IS
    // the application, so the drawer's child speaks the same facts the roster does.
    contractState: row.contractState,
    contractSignedAt: row.contractSignedAt,
    contractDeadlineAt: row.contractDeadlineAt,
    contractDaysLeft: row.contractDaysLeft,
    enrolled: row.enrolled,
    eviFailed: row.eviFailed,
    eviFailedManually: row.eviFailedManually,
  };
}

function ariaSortOf(sort: { key: PrefillSortKey; direction: SortDirection } | null, key: PrefillSortKey): 'ascending' | 'descending' | undefined {
  if (sort === null || sort.key !== key) return undefined;
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

// ---------------------------------------------------------------------------
// Export (#26.6)
// ---------------------------------------------------------------------------

/**
 * «Скачать xlsx» (T.Y18) — the session's own list, exactly what the screen shows
 * (ОВ#5). T.Z14/Z5 makes that literal per venue: the button lives in the online
 * table's header, and the file it asks for is scoped to the venue that header is
 * about (`?venueId=`, v1.8.2). With no id — the cabinet's «Все места» — the whole
 * scope arrives.
 */
function ExportButton({ venueId }: { venueId?: number }) {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const run = async () => {
    setState('busy');
    setMessage(null);
    try {
      await downloadCuratorExport('xlsx', venueId);
      setState('idle');
    } catch (error) {
      setState('error');
      setMessage(error instanceof ApiError ? copyOf(error) : 'Не удалось скачать файл.');
    }
  };

  return (
    <span className="export-button" data-testid="cabinet-export-wrap">
      <Button variant="secondary" size="sm" data-testid="cabinet-export" loading={state === 'busy'} onClick={() => void run()}>
        Скачать xlsx
      </Button>
      {message !== null ? (
        <span className="export-button__error" role="alert" data-testid="cabinet-export-error">
          {message}
        </span>
      ) : null}
    </span>
  );
}

/** The api's refusal, verbatim in wording (its `meta.hint`/`meta.limit` included). */
function copyOf(error: unknown): string {
  return curatorErrorCopy(error);
}
